import { useEffect, useRef, useState } from "react";
import { useStore } from "../store.ts";
import { useDebounced } from "../hooks.ts";
import { Karta, Odznaka, Przycisk } from "../ui.tsx";
import type { PmView } from "./PanstwaMiasta.tsx";

/**
 * Ekran pisania odpowiedzi — najważniejsze 3 minuty całej gry.
 *
 * Trzy rzeczy, na których wszystko tu stoi:
 *   * odpowiedzi lecą na serwer z opóźnieniem, ale zapisują się lokalnie
 *     natychmiast, więc STOP nie zjada wpisywanego właśnie słowa
 *   * cudzych odpowiedzi nie widać — jedynie ile kto wypełnił pól
 *   * przycisk STOP jest wielki i zawsze na dole, żeby dało się go trafić
 *     kciukiem bez patrzenia
 */
export function Runda({
  gameId,
  pm,
  nicki,
  mojeId,
  onStop,
  onGotowe,
}: {
  gameId: string;
  pm: PmView;
  nicki: Record<string, string>;
  mojeId: string;
  onStop: () => void;
  onGotowe: () => void;
}) {
  const answer = useStore((state) => state.answer);

  /*
   * Lokalna kopia wpisywanych odpowiedzi.
   *
   * Sterowanie polami wprost z widoku serwera powodowałoby, że litera wpisana
   * w chwili nadejścia aktualizacji znika albo kursor skacze na koniec. Widok
   * serwera jest tu tylko wartością początkową.
   */
  const [lokalne, setLokalne] = useState<Record<string, string>>(pm.myAnswers);
  const rundaRef = useRef(pm.roundNo);

  useEffect(() => {
    if (rundaRef.current !== pm.roundNo) {
      rundaRef.current = pm.roundNo;
      setLokalne({});
    }
  }, [pm.roundNo]);

  const wyslij = useDebounced((kategoria: string, wartosc: string) => {
    answer(gameId, kategoria, wartosc);
  }, 400);

  const zmien = (kategoria: string, wartosc: string) => {
    setLokalne((poprzednie) => ({ ...poprzednie, [kategoria]: wartosc }));
    wyslij(kategoria, wartosc);
  };

  // Ten sam próg co w `progress` na serwerze — od dwóch znaków. Inaczej własny
  // licznik przeskakiwałby wcześniej niż ten, który widzą inni.
  const wypelnione = pm.categories.filter(
    (kategoria) => (lokalne[kategoria.key] ?? "").trim().length >= 2,
  ).length;

  const jestemGotowy = pm.ready.includes(mojeId);

  return (
    <>
      <div className="flex items-center justify-center py-2">
        <Losowanie litera={pm.letter} runda={pm.roundNo} />
      </div>

      <div className="space-y-3">
        {pm.categories.map((kategoria, index) => (
          <div key={kategoria.key}>
            <label className="block text-sm text-tekst-slaby mb-1" htmlFor={kategoria.key}>
              {kategoria.label}
            </label>
            <input
              id={kategoria.key}
              value={lokalne[kategoria.key] ?? ""}
              onChange={(event) => zmien(kategoria.key, event.target.value)}
              /* Pierwsze pole dostaje focus samo — sekunda zaoszczędzona
                 na starcie każdej rundy. */
              autoFocus={index === 0}
              autoCapitalize="words"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              /* Enter przeskakuje do następnej kategorii zamiast wysyłać
                 formularz — tak pisze się najszybciej. */
              enterKeyHint={index === pm.categories.length - 1 ? "done" : "next"}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                event.preventDefault();
                const next = pm.categories[index + 1];
                if (next) document.getElementById(next.key)?.focus();
                else (event.target as HTMLInputElement).blur();
              }}
              className="w-full min-h-14 px-4 rounded-2xl bg-panel border border-obrys focus:outline-none focus:border-akcent"
            />
          </div>
        ))}
      </div>

      <Karta>
        <div className="text-sm text-tekst-slaby mb-2">Kto ile ma</div>
        <div className="space-y-2">
          {pm.progress.map((wpis) => (
            <div key={wpis.playerId} className="flex items-center gap-3">
              <span className="w-24 truncate text-sm">{nicki[wpis.playerId] ?? "?"}</span>
              <div className="flex-1 h-2 bg-tlo rounded-full overflow-hidden">
                <div
                  className="h-full bg-akcent transition-all"
                  style={{ width: `${(wpis.filled / Math.max(1, wpis.total)) * 100}%` }}
                />
              </div>
              <span className="tabular text-sm text-tekst-slaby">
                {wpis.filled}/{wpis.total}
              </span>
            </div>
          ))}
        </div>
      </Karta>

      <div className="sticky bottom-0 pt-2 bezpieczny-dol bg-tlo">
        {pm.stoppedBy ? (
          <div className="text-center text-uwaga mb-2">
            {nicki[pm.stoppedBy] ?? "Ktoś"} kliknął STOP — kończymy!
          </div>
        ) : null}

        {pm.settings.endMode === "first-stop" ? (
          <Przycisk
            wariant={pm.stoppedBy ? "gorny" : "glowny"}
            pelna
            onClick={onStop}
            disabled={Boolean(pm.stoppedBy)}
            className="min-h-20 text-2xl"
          >
            STOP
          </Przycisk>
        ) : pm.settings.endMode === "all-ready" ? (
          <Przycisk
            wariant={jestemGotowy ? "gorny" : "glowny"}
            pelna
            onClick={onGotowe}
            disabled={jestemGotowy}
            className="min-h-20 text-2xl"
          >
            {jestemGotowy ? `Czekam (${pm.ready.length}/${pm.progress.length})` : "Gotowe"}
          </Przycisk>
        ) : (
          <div className="text-center text-tekst-slaby py-4">
            <Odznaka>{wypelnione} z {pm.categories.length} wypełnione</Odznaka>
          </div>
        )}
      </div>
    </>
  );
}

/** Litery, przez które przewija się losowanie — polski alfabet bez ogonków i bez Q, V, X. */
const ALFABET = "ABCDEFGHIJKLMNOPRSTUWYZ";

/**
 * Losowanie litery.
 *
 * Wyzwalaczem jest **numer rundy**, nie sama litera. Ta sama litera dwa razy
 * pod rząd zdarza się przy dwudziestu kilku znakach częściej, niż się wydaje,
 * a bez tej ostrożności taka runda zaczynałaby się bez żadnego sygnału —
 * dokładnie ten sam błąd, który wcześniej zastygał kośćmi w Yahtzee.
 *
 * Przewijanie trwa 720 ms i zwalnia ku końcowi. Dłużej się nie da: runda ma
 * trzy minuty i każda sekunda oglądania animacji to sekunda mniej na pisanie.
 */
function Losowanie({ litera, runda }: { litera: string | null; runda: number }) {
  const [pokazywana, setPokazywana] = useState(litera);
  const [przewija, setPrzewija] = useState(false);
  const [krok, setKrok] = useState(0);

  useEffect(() => {
    if (!litera) {
      setPokazywana(null);
      return;
    }

    const skrocone =
      typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (skrocone) {
      setPokazywana(litera);
      return;
    }

    setPrzewija(true);
    const timery: number[] = [];
    // Odstępy rosną: 40, 48, 58… — koniec wygląda na zwalnianie, a nie na ucięcie.
    let czas = 0;
    for (let i = 0; i < 14; i += 1) {
      czas += 40 + i * 7;
      timery.push(
        window.setTimeout(() => {
          setPokazywana(ALFABET[Math.floor(Math.random() * ALFABET.length)] as string);
          setKrok((n) => n + 1);
        }, czas),
      );
    }
    timery.push(
      window.setTimeout(() => {
        setPokazywana(litera);
        setPrzewija(false);
        setKrok((n) => n + 1);
      }, czas + 60),
    );

    return () => timery.forEach(window.clearTimeout);
    // Zależność od rundy, nie od litery — patrz komentarz nad komponentem.
  }, [runda]);

  return (
    <div
      key={krok}
      className={[
        "text-7xl font-bold tracking-tight",
        przewija ? "litera-przewijanie text-tekst-slaby" : "litera-ladowanie text-akcent",
      ].join(" ")}
    >
      {pokazywana}
    </div>
  );
}
